<template>
    <page :busy="busy" :tabs="tabs">
        <grid
            ref="grid"
            :defaultColDef="defaultColDef"
            :autoGroupColumnDef="autoGroupColumnDef"
            :columnDefs="columnDefs"
            :treeData="true"
            :getDataPath="getDataPath"
            :isExternalFilterPresent="isExternalFilterPresent"
            :doesExternalFilterPass="doesExternalFilterPass"
            :rowData="properties"
            :canDragRow="canDragRow"
            :canDropRow="canDropRow"
            :saveRowOrder="saveRowOrder"
            :enableCellTextSelection="false"
        >
            <responsive-dropdown :icon="['fas', 'arrow-down-short-wide']" :items="reorderItems" variant="primary"
                >Reorder</responsive-dropdown
            >
            <form-square-switch
                class="mr-1 show-disabled-toggle"
                v-model="showDisabled"
                :labels="{
                    checked: 'Show Disabled',
                    unchecked: 'Show Disabled',
                }"
                :isToolbar="true"
                :sync="true"
            />
            <b-button-group aria-label="Export table data">
                <responsive-button
                    :icon="['fas', 'file-excel']"
                    href="/app/property/property/excel/"
                    variant="primary"
                ></responsive-button>
            </b-button-group>
            <responsive-button :icon="['fas', 'plus']" modal="create-property-modal" variant="primary"
                >Add Property</responsive-button
            >
            <modal id="modal-reorder-name" title="Reorder Alphabetically" @ok="reorder('name')"
                >All properties will now be displayed alphabetically. Current display order will be overwritten.
                Continue?</modal
            >
            <modal id="modal-reorder-type" title="Reorder By Type" @ok="reorder('type')"
                >All properties will now be displayed alphabetically by property type. Current display order will be
                overwritten. Continue?</modal
            >
            <modal id="modal-property-enable-error" title="Error" ok-only><span v-html="enableError" /></modal>
        </grid>
    </page>
</template>

<script>
    import { mapGetters } from 'vuex';
    import API from './../../../api';
    import Page from '../../generic/page/page.vue';
    import Grid from '../../generic/grid/grid.vue';
    import ActionsCellRenderer from './actions-cell-renderer.vue';
    import EnabledCellRenderer from './enabled-cell-renderer.vue';
    import ResponsiveButton from '../../generic/responsive-button/responsive-button.vue';
    import ResponsiveDropdown from '../../generic/responsive-dropdown/responsive-dropdown.vue';
    import FormSquareSwitch from '../../generic/form-square-switch/form-square-switch.vue';
    import { formatResponseError } from '../../../utils/api';
    import Modal from '../../generic/modal/modal.vue';
    import { removeScrollMixin } from '../../../mixins/remove-scroll-mixin';

    export default {
        name: 'PropertyList',
        components: {
            Page,
            Grid,
            ResponsiveButton,
            ResponsiveDropdown,
            FormSquareSwitch,
            Modal,
            actionsCellRenderer: ActionsCellRenderer,
            enabledCellRenderer: EnabledCellRenderer,
        },
        mixins: [removeScrollMixin],
        data() {
            const defaultShowDisabled = this.$store.getters['propertyListLocalStorage/showDisabled'];
            return {
                properties: [],
                showDisabled: defaultShowDisabled,
                tabs: [
                    {
                        title: 'Property List',
                        route: '/app/property/list/',
                        active: true,
                    },
                ],
                busy: false,
                defaultColDef: {
                    minWidth: 80,
                    sortable: false,
                    cellClass: ({ data }) => (data.disabled ? 'text-muted' : ''),
                },
                autoGroupColumnDef: {
                    flex: 1,
                    minWidth: 80,
                    resizable: true,
                    sortable: false,
                    headerName: 'ID',
                    cellStyle: { textAlign: 'center' },
                    cellRendererParams: {
                        innerRenderer: ({ data }) => data.id,
                    },
                },
                getDataPath: (data) => data.id_hierarchy,
                enableError: '',
            };
        },
        computed: {
            ...mapGetters('category', ['categoryIds', 'categoryNameById']),
            columnDefs() {
                return [
                    {
                        type: 'orderColumn',
                        cellRenderer: ({ data, value }) =>
                            !data.disabled && data.property_type !== 'Room type' ? value : '',
                        editable: ({ data }) => !data.disabled && data.property_type !== 'Room type',
                        cellClass: ({ data }) =>
                            data.disabled
                                ? 'text-muted'
                                : data.property_type !== 'Room type'
                                ? 'text-primary lx-editable-cell'
                                : '',
                        rowDrag: ({ data }) => !data.disabled && data.property_type !== 'Room type',
                        rowDragText: ({ defaultTextValue, rowNode }) => `${defaultTextValue} ${rowNode.data.name}`,
                    },
                    {
                        field: 'creation_date',
                        type: 'dateTimeColumn',
                        headerName: 'Created',
                        minWidth: 120,
                    },
                    {
                        type: 'editableColumn',
                        field: 'name',
                        headerName: 'Name',
                        sort: 'asc',
                        flex: 3,
                        minWidth: 150,
                        editable: ({ data }) => !data.disabled,
                        valueSetter: ({ oldValue, newValue, node, data }) => {
                            (async () => {
                                try {
                                    await API.put(`/app/property/property/${data.id}/`, {
                                        name: newValue,
                                    });
                                } catch (e) {
                                    const { generic } = formatResponseError(e);
                                    this.errorToast({
                                        text: generic || 'Could not save name',
                                    });
                                    data.name = oldValue;
                                    node.setData(data);
                                }
                            })();
                            data.name = newValue;
                            return true;
                        },
                        cellClass: ({ data }) => {
                            const classList = [];
                            let extSource = data.external_source;
                            if (extSource) {
                                extSource = extSource.replace(/[^a-z]/g, '');
                            }
                            if (extSource) {
                                classList.push('lx-ext');
                                classList.push(`lx-ext-${extSource}`);
                            }
                            if (data.disabled) {
                                classList.push('text-muted');
                            } else {
                                classList.push('text-primary');
                                classList.push('lx-editable-cell');
                            }
                            return classList;
                        },
                    },
                    {
                        type: 'editableColumn',
                        field: 'marketing_title',
                        headerName: 'Marketing Title',
                        flex: 3,
                        minWidth: 150,
                        editable: ({ data }) => !data.disabled,
                        valueSetter: ({ oldValue, newValue, node, data }) => {
                            (async () => {
                                try {
                                    await API.put(`/app/property/property/${data.id}/`, {
                                        marketing_title: newValue,
                                    });
                                } catch (error) {
                                    this.errorToast({
                                        text: 'Could not save marketing title.',
                                    });
                                    data.marketing_title = oldValue;
                                    node.setData(data);
                                }
                            })();
                            data.marketing_title = newValue;
                            return true;
                        },
                        cellClass: ({ data }) => (data.disabled ? 'text-muted' : 'text-primary lx-editable-cell'),
                    },
                    {
                        field: 'property_type',
                        headerName: 'Type',
                        minWidth: 90,
                    },
                    {
                        field: 'category_id',
                        headerName: 'Category',
                        flex: 2,
                        minWidth: 120,
                        cellRenderer: ({ data, value }) =>
                            data.property_type !== 'Room type' ? this.categoryNameById(value) : '',
                        editable: ({ data }) => !data.disabled && data.property_type !== 'Room type',
                        cellClass: ({ data }) =>
                            data.disabled
                                ? 'text-muted'
                                : data.property_type !== 'Room type'
                                ? 'text-primary lx-editable-cell'
                                : '',
                        cellEditor: 'agRichSelectCellEditor',
                        cellEditorParams: () => ({
                            values: this.categoryIds,
                            formatValue: (values) => {
                                return this.categoryNameById(values);
                            },
                        }),
                        valueSetter: ({ oldValue, newValue, node, data }) => {
                            (async () => {
                                try {
                                    await API.put(`/app/property/property/${data.id}/`, {
                                        category_id: newValue,
                                    });
                                } catch (error) {
                                    this.errorToast({
                                        text: 'Could not save category.',
                                    });
                                    data.category_id = oldValue;
                                    node.setData(data);
                                }
                            })();
                            data.category_id = newValue;
                            return true;
                        },
                    },
                    {
                        headerName: 'Bdrm/ Baths',
                        valueGetter: ({ data }) =>
                            data.property_type !== 'Inn' ? `${data.n_bedrooms}/${data.n_baths}` : '',
                    },
                    {
                        headerName: 'Beds/ Sleeps',
                        valueGetter: ({ data }) =>
                            data.property_type !== 'Inn' ? `${data.n_beds}/${data.n_sleeps}` : '',
                    },
                    {
                        cellRenderer: 'enabledCellRenderer',
                        field: 'disabled',
                        headerName: 'Enable',
                        valueGetter: ({ data }) => !data.disabled,
                        cellRendererParams: {
                            onChange: async ({ node, api }, { newValue, disabledReason, disabledReasonDetails }) => {
                                const row = node.data;
                                const disabled = !newValue;
                                this.busy = true;
                                try {
                                    const payload = { disabled };
                                    if (disabled) {
                                        payload.disabled_reason = disabledReason;
                                        payload.disabled_reason_details = disabledReasonDetails;
                                    }
                                    await API.put(`/app/property/property/${row.id}/`, payload);
                                    var message = 'Property successfully ' + (disabled ? 'disabled.' : 'enabled.');
                                    this.successToast({ text: message });
                                } catch (error) {
                                    this.busy = false;
                                    const disable = disabled ? 'disable' : 'enable';
                                    const response = error.response;
                                    const detail =
                                        response && response.data && response.data.detail
                                            ? ' ' + response.data.detail
                                            : '';
                                    this.enableError = `Could not ${disable} property.${detail}`;
                                    this.$bvModal.show('modal-property-enable-error');
                                    api.redrawRows({ rowNodes: [node] });
                                    return;
                                }
                                const updateRows = [row];
                                const updateNodes = [node];
                                const grid = this.$refs.grid;
                                row.disabled = disabled;
                                row.order = disabled ? 999999 : grid.getMaxOrder(node) + 1;
                                if (node.level > 1) {
                                    const parentNode = node.parent;
                                    const parentRow = parentNode.data;
                                    let updateParent = false;
                                    if (disabled) {
                                        if (
                                            parentNode.childrenAfterGroup.findIndex(
                                                (item) => item.data.id !== row.id && !item.data.disabled
                                            ) < 0
                                        ) {
                                            updateParent = !parentRow.disabled;
                                        }
                                    } else {
                                        updateParent = parentRow.disabled;
                                    }
                                    if (updateParent) {
                                        parentRow.disabled = disabled;
                                        parentRow.order = disabled ? 999999 : grid.getMaxOrder(parentNode) + 1;
                                        updateRows.push(parentRow);
                                        updateNodes.push(parentNode);
                                    }
                                } else if (disabled && node.allChildrenCount) {
                                    node.childrenAfterGroup.forEach((childNode) => {
                                        const childRow = childNode.data;
                                        childRow.disabled = true;
                                        childRow.order = 999999;
                                        updateRows.push(childRow);
                                        updateNodes.push(childNode);
                                    });
                                }
                                api.applyTransaction({ update: updateRows });
                                api.redrawRows({ rowNodes: updateNodes });
                                this.busy = false;
                            },
                        },
                    },
                    {
                        field: 'n_units',
                        headerName: 'Units',
                    },
                    {
                        headerName: '',
                        minWidth: 190,
                        resizable: false,
                        cellStyle: { textAlign: 'center' },
                        cellRenderer: 'actionsCellRenderer',
                    },
                ];
            },
            canDragRow() {
                return (row) => !row.disabled;
            },
            canDropRow() {
                return ({ overRow }) => overRow.property_type !== 'Room type';
            },
            saveRowOrder() {
                return async ({ row, order, onSuccess, onError }) => {
                    this.busy = true;
                    try {
                        await API.put(`/app/property/property/${row.id}/`, {
                            order,
                        });
                    } catch (error) {
                        this.busy = false;
                        onError(error);
                        return;
                    }
                    onSuccess();
                    this.busy = false;
                };
            },
            reorderItems() {
                return [
                    {
                        icon: ['fas', 'arrow-down-a-z'],
                        label: 'Alphabetically',
                        modal: 'modal-reorder-name',
                    },
                    {
                        icon: ['fas', 'arrow-down-short-wide'],
                        label: 'By Type',
                        modal: 'modal-reorder-type',
                    },
                ];
            },
            isExternalFilterPresent() {
                return () => false;
            },
            doesExternalFilterPass() {
                return (node) => !node.data.disabled;
            },
        },
        mounted() {
            (async () => {
                this.busy = true;
                try {
                    await this.$store.dispatch('category/fetchCategories');
                } catch (err) {
                    this.errorToast({ text: 'Could not load category data.' });
                }
                await this.fetchProperties();
                this.busy = false;
            })();
        },
        methods: {
            async fetchProperties() {
                try {
                    const response = await API.get('/app/property/property/', {
                        params: { show_disabled: this.showDisabled },
                    });
                    this.properties = response.data;
                } catch (err) {
                    this.errorToast({ text: 'Could not load property data.' });
                }
            },
            async reorder(by) {
                this.busy = true;
                try {
                    await API.put(`/app/property/property/reorder/`, { by });
                } catch (error) {
                    this.busy = false;
                    this.errorToast({ text: 'Could not reorder.' });
                    return;
                }
                await this.fetchProperties();
                this.busy = false;
            },
        },
        watch: {
            showDisabled(value) {
                this.$store.dispatch('propertyListLocalStorage/changeShowDisabled', value);
                this.fetchProperties();
            },
        },
    };
</script>
<style lang="scss" scoped>
    @import '~bootstrap/scss/_functions.scss';
    @import '~bootstrap/scss/_variables.scss';
    @import '~bootstrap/scss/mixins/_breakpoints.scss';

    .lx-button-import::before {
        content: '';
        display: inline-block;
        margin: 0.2rem 0.3rem 0 0;
        width: 1.2rem;
        height: 1.2rem;
        background-repeat: no-repeat;
        background-size: contain;
        vertical-align: text-bottom;
    }

    .lx-button-import-homeaway::before {
        background-image: url(logo-homeaway-inverted.svg);
    }

    .lx-button-import-airbnb::before {
        background-image: url(logo-airbnb-inverted.svg);
    }

    //:deep(div[col-id="ag-Grid-AutoColumn"] .ag-header-cell-text {
    //    padding: 15px;
    //}

    :deep(.ag-theme-alpine .ag-ltr .ag-row-group-leaf-indent) {
        margin-left: 0;
    }

    :deep(.ag-cell.lx-ext::before) {
        content: '';
        display: inline-block;
        margin: 0.2rem 0.3rem 0 0;
        width: 1rem;
        height: 1rem;
        background-repeat: no-repeat;
        background-size: contain;
        vertical-align: text-bottom;
    }

    :deep(.ag-cell.lx-ext-homeaway::before) {
        background-image: url(logo-homeaway.svg);
    }

    :deep(.ag-cell.lx-ext-airbnb::before) {
        background-image: url(logo-airbnb.svg);
    }

    :deep(.ag-cell.lx-ext-booking::before) {
        background-image: url(logo-booking.svg);
    }

    :deep(.ag-cell.lx-ext-expedia::before) {
        background-image: url(logo-expedia.svg);
    }

    .show-disabled-toggle {
        width: 134px;
        @include media-breakpoint-down(lg) {
            width: 152px;
        }
    }
</style>
